import { useCallback, useState, useEffect } from "react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import { Crop, MagnifyingGlassPlus } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

/**
 * Profile photo crop-before-upload dialog.
 *
 * Accepts a raw File → shows a square 1:1 cropper with pan + zoom → returns a
 * cropped Blob via `onCropped` (same mimeType, square, no stretch, circular
 * preview matches how the Avatar renders it on every page).
 *
 * Usage:
 *   <PhotoCropDialog
 *     file={pickedFile}
 *     onCropped={(blob) => uploadToServer(blob)}
 *     onClose={() => setPickedFile(null)}
 *   />
 *
 * Pure client-side — no deps on backend.
 */
export default function PhotoCropDialog({ file, onCropped, onClose }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  }, [file]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      toast.error("Could not read image — try another file");
      return;
    }
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, file?.type || "image/jpeg");
      onCropped?.(blob);
    } catch (err) {
      toast.error("Failed to crop image");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!file} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="photo-crop-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crop className="w-4 h-4 text-primary" weight="fill" />
            Crop photo
          </DialogTitle>
          <DialogDescription>
            Drag to reposition, use the slider to zoom. Your avatar will be cropped into a circle
            — preview below matches how it appears everywhere.
          </DialogDescription>
        </DialogHeader>

        <div className="relative w-full h-72 bg-muted/40 rounded-lg overflow-hidden border border-border">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              objectFit="cover"
            />
          )}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <MagnifyingGlassPlus className="w-4 h-4 text-muted-foreground shrink-0" />
          <Slider
            value={[zoom]} min={1} max={4} step={0.01}
            onValueChange={(v) => setZoom(v[0])}
            className="flex-1"
            data-testid="photo-crop-zoom"
          />
          <span className="text-[11px] text-muted-foreground tabular-nums w-10 text-right">
            {zoom.toFixed(2)}×
          </span>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="photo-crop-cancel">Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || !croppedAreaPixels}
            data-testid="photo-crop-confirm"
            className="bg-primary text-primary-foreground"
          >
            {saving ? "Cropping…" : "Use this crop"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Off-screen canvas crop. Keeps native resolution up to the target crop box —
 * for a profile photo a 512px output is generous (the largest avatar on the
 * site is ~144px so this is visually lossless).
 */
async function getCroppedBlob(imageSrc, area, mimeType = "image/jpeg") {
  const image = await loadImage(imageSrc);
  const OUTPUT_MAX = 512;
  const scale = Math.min(1, OUTPUT_MAX / Math.max(area.width, area.height));
  const outW = Math.round(area.width * scale);
  const outH = Math.round(area.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    area.x, area.y, area.width, area.height,
    0, 0, outW, outH,
  );

  // Prefer JPEG for photos (smaller), PNG only if the source was PNG (alpha).
  const targetType = mimeType === "image/png" ? "image/png" : "image/jpeg";
  return await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), targetType, 0.92);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
